import React from 'react';
import { connect } from 'react-redux';
import { MdInfoOutline, MdWarningAmber } from 'react-icons/md';
import { aktiviereLokalesSpeichern, setScreen, appScreens } from '../store/appNavigation/actions';
import { countOffeneTeilnahmen } from '../store/punkteMeldung/commonUtils';

/**
 * Sagt an der Arbeitsstelle, ob diese Sitzung den Browser ueberlebt.
 *
 * Der Speichermodus stand bisher nur auf der Startseite - die sieht aber nicht, wer aus dem
 * Portal der Kammer mit `?vnr=...` direkt auf seiner Veranstaltung landet. Ausgerechnet dort
 * haengt daran alles: erfasste, noch nicht gemeldete Teilnahmen sind die Warteschlange, und ohne
 * Speicherung ueberleben sie kein Neuladen.
 *
 * **Einschalten ja, ausschalten nein.** Einschalten loescht nichts und ist genau das, was der
 * wiederkehrende Nutzer will. Ausschalten ruft `eivngCore.resetSettings()` - localStorage.clear()
 * plus Reload, also alle Veranstaltungen und alle offenen Teilnahmen weg. Dieser Weg bleibt der
 * Startseite vorbehalten und bekommt hier keinen zweiten Eingang.
 *
 * Kein reactstrap-`Alert`: dessen `role="alert"` laesst sich nicht ueberschreiben und
 * unterbricht den Screenreader mitten im Satz. `role="status"` mit `aria-live="polite"` wartet ab.
 */
export const speichermodusHinweis = (gespeichert, anzahlOffen, kontext) => {
	// Der erledigte Zustand ist kein Hinweis mehr, sondern eine Bestaetigung - eine ruhige Zeile
	// ohne Kasten. Laut wird nur, was offen ist, und richtig laut nur mit offenen Teilnahmen.
	if(gespeichert) return { kopf: "Auf diesem Gerät gespeichert", satz: "", farbe: "", einschaltbar: false };
	if(anzahlOffen > 0) return {
		kopf: anzahlOffen === 1
			? "Eine Teilnahme ist erfasst und noch nicht gemeldet."
			: `${anzahlOffen} Teilnahmen sind erfasst und noch nicht gemeldet.`,
		// Nicht "beim Schliessen des Browsers": loadState findet ohne gespeicherten Zustand
		// nichts, ein simples F5 raeumt genauso ab. Die engere Formulierung waere eine falsche
		// Zusage an der Stelle, an der es teuer wird.
		satz: anzahlOffen === 1
			? "Beim Neuladen oder Schließen dieser Seite geht sie verloren."
			: "Beim Neuladen oder Schließen dieser Seite gehen sie verloren.",
		farbe: "warning", einschaltbar: true };
	return {
		kopf: kontext === 'liste'
			? "Geladene Veranstaltungen und offene Teilnahmen bleiben nur für diesen Besuch erhalten."
			: "Diese Veranstaltung ist nur für diesen Besuch geladen.",
		satz: "", farbe: "secondary", einschaltbar: true };
};

const SpeichermodusHinweis = (props) => {
	const { gespeichert, anzahlOffen, kontext = 'veranstaltung', einschalten, zurStartseite } = props;
	// Nur fuer diesen Bildschirm und nur fuer diesen Fall - im Store haette es nichts zu suchen,
	// und gespeichert werden koennte es ja gerade nicht.
	const [fehlgeschlagen, setFehlgeschlagen] = React.useState(false);
	const hinweis = speichermodusHinweis(gespeichert, anzahlOffen, kontext);

	if(gespeichert) return (
		<div className="small text-body-secondary mb-2" role="status" aria-live="polite">
			✓ {hinweis.kopf}{' '}
			{/* Trenner und Knopf zusammen: sonst bleibt auf schmalen Geraeten das Trennzeichen
			    allein am Zeilenende haengen. */}
			<span className="text-nowrap">
				<span aria-hidden="true">· </span>
				<button type="button" className="btn btn-link btn-sm p-0 align-baseline text-decoration-none"
					onClick={zurStartseite}>Einstellungen ändern</button>
			</span>
		</div>
	);

	const Zeichen = hinweis.farbe === "warning" ? MdWarningAmber : MdInfoOutline;
	return (
		<div className={`alert alert-${hinweis.farbe} py-2 mb-2 d-flex align-items-start gap-2`}
			role="status" aria-live="polite">
			<Zeichen fontSize="1.3em" className="flex-shrink-0 mt-1" aria-hidden="true" />
			<div>
				<b>{hinweis.kopf}</b>{hinweis.satz && <> {hinweis.satz}</>}
				<div className="mt-2">
					<button type="button" className="btn btn-secondary btn-sm"
						onClick={() => setFehlgeschlagen(!einschalten())}>Auf diesem Gerät speichern</button>
				</div>
				<div className="small mt-1">
					{ fehlgeschlagen
						? <b>Dieser Browser lässt das Speichern nicht zu.</b>
						: <>Ab dann liegen {kontext === 'liste' ? "die Veranstaltungen" : "die Veranstaltung"},
							erfasste Teilnahmen und eingetragene Namen im Speicher dieses Browsers.</> }
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	gespeichert: Boolean(state.appNavigation.saveStateToLocalStorage),
	anzahlOffen: countOffeneTeilnahmen(state, ownProps.vnr)
});

const mapDispatchToProps = (dispatch) => ({
	einschalten: () => dispatch(aktiviereLokalesSpeichern()),
	zurStartseite: () => dispatch(setScreen(appScreens.startPage))
});

export default connect(mapStateToProps, mapDispatchToProps)(SpeichermodusHinweis);
